<template>
	<AppToggleButtons
		v-if="hasProfiles"
		class="min-w-0 shrink"
		v-model="selectedProfileId"
		:options="profileOptions"
	/>
</template>

<script setup lang="ts">
import { getGridPresets, UserGridPreset } from "@q-food/q-food";
import { GridApi } from "ag-grid-community";
import { processFilterModel } from "~utils/Grids/GridProcessFilter.ts";
import { useResourceEventBus } from "~utils/useResourceEventBus.ts";
import AppToggleButtons from "~components/AppToggleButtons.vue";

// General-purpose profile ("profielen") toggle buttons for any AppDataGrid. Lists this grid's saved
// profiles and applies the picked one's column layout + filters to the live grid — the same apply
// logic the "Profielen" side panel uses. Rendered by AppDataGrid only when the user opts in via the
// `showGridProfileButtons` setting (default off).
const props = defineProps<{
	gridKey: string;
	gridApi: GridApi | null;
}>();

const profiles = ref<UserGridPreset[]>([]);
const selectedProfileId = ref<string>("");

const hasProfiles = computed(() => profiles.value.length > 0);
const profileOptions = computed<Record<string, string>>(() =>
	Object.fromEntries(profiles.value.map((profile) => [profile.id, profile.name])),
);

async function loadProfiles() {
	try {
		profiles.value = await getGridPresets(props.gridKey);
	} catch {
		profiles.value = [];
	}
}

const profilesBus = useResourceEventBus(`grid-preset:${props.gridKey}`);
onMounted(() => {
	loadProfiles();
	profilesBus.on(loadProfiles);
});
onUnmounted(() => profilesBus.off(loadProfiles));

watch(selectedProfileId, (id) => {
	const api = props.gridApi;
	if (!api || !id) return;

	const profile = profiles.value.find((p) => p.id === id);
	if (!profile) return;

	api.applyColumnState({ state: JSON.parse(profile.columnState), applyOrder: true });
	api.setFilterModel(processFilterModel(JSON.parse(profile.filterState)));
});
</script>
